<template>
  <div class="container-fluid sale-detail">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando nota de pedido...</p>
    </div>

    <div v-else class="sale-detail-content">
      <!-- View header -->
      <div class="view-header my-4">
        <nav class="view-header__breadcrumb" aria-label="breadcrumb">
          <router-link :to="{ name: 'Notas de pedido' }">Notas de pedido</router-link>
          <i class="bi bi-chevron-right view-header__breadcrumb-sep"></i>
          <template v-if="ordernote.pos && ordernote.number">
            <span class="view-header__breadcrumb-current">
              {{ padLeft(ordernote.pos, 4) }}-{{ padLeft(ordernote.number, 8) }}
            </span>
          </template>
          <span v-else class="view-header__breadcrumb-current">#{{ orderNoteId }}</span>
        </nav>

        <div class="view-header__main">
          <div class="view-header__info">
            <h1 class="view-header__title">Detalle de nota de pedido</h1>
            <p class="view-header__subtitle">Información detallada de la nota de pedido.</p>
          </div>

          <div class="view-header__actions">
            <DownloadDoc
              :pdfurl="pdfurl"
              :open="true"
              :filename="ordernote.client.name + ordernote.created_at + '.pdf'"
            >
              <button type="button" class="btn view-header__btn">
                <i class="bi bi-printer"></i>
                Imprimir
              </button>
            </DownloadDoc>
            <DownloadDoc
              :pdfurl="pdfurl"
              :open="false"
              :filename="ordernote.client.name + ordernote.created_at + '.pdf'"
            >
              <button type="button" class="btn view-header__btn view-header__btn--primary">
                <i class="bi bi-download"></i>
                Descargar PDF
              </button>
            </DownloadDoc>
          </div>
        </div>
      </div>

      <!-- Header card -->
      <div class="card invoice-header-card mb-4">
        <div class="invoice-header-main">
          <div class="invoice-type-block">
            <div class="invoice-type-icon">
              <i class="bi bi-file-earmark-text"></i>
            </div>
            <div class="invoice-type-info">
              <span class="invoice-type-label">Tipo de documento</span>
              <span class="invoice-type-value">
                Nota de pedido<template v-if="ordernote.letter"> {{ ordernote.letter }}</template>
              </span>
              <span v-if="ordernote.letter && ordernote.letter !== 'P'" class="invoice-type-badge">
                Original
              </span>
            </div>
          </div>

          <div class="invoice-client-block">
            <div class="invoice-client-title">
              <span class="client-title-icon">
                <i class="bi bi-person"></i>
              </span>
              <span>Datos del cliente</span>
            </div>

            <div class="invoice-client-grid">
              <div class="client-field">
                <span class="field-label">Nombre / Razón social</span>
                <span class="field-value">{{ ordernote.client.name || '—' }}</span>
              </div>

              <div class="client-field">
                <span class="field-label">
                  <i class="bi bi-calendar3 field-icon"></i>
                  Fecha de emisión
                </span>
                <span class="field-value">{{ createdDateFormatted || '—' }}</span>
              </div>

              <div v-if="ordernote.number && ordernote.pos" class="client-field">
                <span class="field-label">
                  <i class="bi bi-file-text field-icon"></i>
                  N° de nota
                </span>
                <span class="field-value">
                  {{ padLeft(ordernote.pos, 4) }}-{{ padLeft(ordernote.number, 8) }}
                </span>
              </div>

              <div v-if="ordernote.client_cuit" class="client-field">
                <span class="field-label">
                  <i class="bi bi-card-text field-icon"></i>
                  CUIT
                </span>
                <span class="field-value">{{ ordernote.client_cuit }}</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasSecondaryInfo" class="invoice-header-secondary">
          <div v-if="clerkname" class="secondary-item">
            <span class="secondary-label">Vendedor</span>
            <span class="secondary-value">{{ clerkname }}</span>
          </div>
          <div v-if="ordernote.response && ordernote.response['CAE']" class="secondary-item">
            <span class="secondary-label">CAE</span>
            <span class="secondary-value secondary-value--mono">{{ ordernote.response['CAE'] }}</span>
            <span v-if="caeExpDateFormatted" class="secondary-sublabel">
              (vto. {{ caeExpDateFormatted }})
            </span>
          </div>
        </div>
      </div>

      <!-- Items + totals -->
      <div class="row">
        <!-- Items table -->
        <div class="col-md-8 mb-4 mb-md-0">
          <div class="card items-card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-box-seam"></i>
                </span>
                <h5 class="mb-0">Items</h5>
              </div>
              <div v-if="ordernote.ordernote_details && ordernote.ordernote_details.length > 0" class="items-summary">
                <span class="items-summary-item">
                  Total de ítems: <strong>{{ ordernote.ordernote_details.length }}</strong>
                </span>
                <span v-if="totalUnits > 0" class="items-summary-sep">|</span>
                <span v-if="totalUnits > 0" class="items-summary-item">
                  Total de unidades: <strong>{{ totalUnits }}</strong>
                </span>
              </div>
            </div>
            <div class="card-body p-4">
              <div class="table-responsive">
                <table class="table table-sm table-hover items-table mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Item</th>
                      <th class="col-small text-center">Cantidad</th>
                      <th class="col-small text-right">Precio unit.</th>
                      <th class="col-small text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(value, idx) in ordernote.ordernote_details"
                      :key="idx"
                    >
                      <td>{{ value.description }}</td>
                      <td class="text-center">{{ value.quantity }}</td>
                      <td class="text-right">{{ formatoDinamicoMoneda(value.price) }}</td>
                      <td class="text-right font-weight-bold">
                        {{ formatoDinamicoMoneda(value.price * value.quantity) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Totals panel -->
        <div class="col-md-4">
          <div class="card totals-card mb-4">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-cash-coin"></i>
                </span>
                <h5 class="mb-0">Totales de la nota</h5>
              </div>
            </div>
            <div class="card-body">
              <div class="total-row">
                <span class="label">Subtotal</span>
                <span class="value">{{ formatoDinamicoMoneda(ordernote.subtotal) }}</span>
              </div>

              <div v-if="ordernote.discount > 0" class="total-row">
                <span class="label">Descuento</span>
                <span class="value text-success">
                  − {{ formatoDinamicoMoneda(ordernote.discount) }}
                </span>
              </div>

              <template v-if="ordernote.letter === 'A' || ordernote.letter === 'M'">
                <div
                  v-for="(tax, key) in ordernote.taxes"
                  :key="key"
                  class="total-row"
                >
                  <span class="label">IVA {{ key }}%</span>
                  <span class="value">{{ formatoDinamicoMoneda(tax) }}</span>
                </div>
              </template>

              <div class="totals-divider"></div>

              <div class="total-row total-final">
                <span class="label">Total</span>
                <span class="value">{{ formatoDinamicoMoneda(ordernote.total) }}</span>
              </div>
            </div>
          </div>

          <div v-if="ordernote.notes" class="card notes-card">
            <div class="card-header">
              <div class="card-title-group">
                <span class="card-title-icon">
                  <i class="bi bi-sticky"></i>
                </span>
                <h6 class="mb-0">Nota para el cliente</h6>
              </div>
            </div>
            <div class="card-body">
              <p class="mb-0 note-text">{{ ordernote.notes }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DownloadDoc from "../../global/DownloadDoc";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";

export default {
  components: {
    DownloadDoc,
  },
  mixins: [formatMoneyMixin],
  props: ["orderNoteId"],
  data() {
    return {
      loading: true,
      pdfurl: process.env.VUE_APP_BASE_URL + '/api/ordernotes/onPDF/' + this.orderNoteId,
      ordernote: {
        letter: null,
        pos: null,
        number: null,
        client_id: null,
        subtotal: null,
        discount: null,
        taxes: null,
        total: null,
        notes: '',
        ordernote_details: [],
        client: {
          name: null,
          clerk: { name: null },
        },
      },
    };
  },
  computed: {
    createdDateFormatted() {
      if (!this.ordernote.created_at) return '';
      return new Date(this.ordernote.created_at).toLocaleDateString('es-AR');
    },
    clerkname() {
      return this.ordernote.client.clerk ? this.ordernote.client.clerk.name : '';
    },
    caeExpDateFormatted() {
      const raw = this.ordernote.response && this.ordernote.response['CAEFchVto'];
      if (!raw) return '';
      const str = String(raw);
      if (/^\d{8}$/.test(str)) {
        return `${str.slice(6, 8)}/${str.slice(4, 6)}/${str.slice(0, 4)}`;
      }
      const d = new Date(str);
      return isNaN(d) ? str : d.toLocaleDateString('es-AR');
    },
    hasSecondaryInfo() {
      return (
        !!this.clerkname ||
        (this.ordernote.response && this.ordernote.response['CAE'])
      );
    },
    totalUnits() {
      if (!Array.isArray(this.ordernote.ordernote_details)) return 0;
      return this.ordernote.ordernote_details.reduce((sum, item) => {
        const q = Number(item.quantity) || 0;
        return sum + q;
      }, 0);
    },
  },
  mounted() {
    this.getOrderNoteDetail(this.orderNoteId);
  },
  methods: {
    getOrderNoteDetail(orderNoteId) {
      this.loading = true;
      this.$http
        .get('/api/ordernotes/getOrdernote/' + orderNoteId)
        .then(response => {
          if (response.data.success) {
            this.ordernote = response.data.data;
          }
        })
        .catch(error => {
          console.error('Error al cargar nota de pedido:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    padLeft(number, length) {
      return number.toString().padStart(length, '0');
    },
  },
};
</script>
